import { ArrowRight, Check, PartyPopper } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { SaveStatus } from "@/components/SaveStatus";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils/cn";
import { thematicClasses } from "@/lib/thematic";
import { useAutosave } from "@/lib/hooks/useAutosave";

import {
  bulkSubmitAnswers,
  goalActionsQuery,
  goalAnswersQuery,
  invalidateAfterAnswersSaved,
  useGoalActions,
  useGoalAnswers,
  type BulkAnswerInput,
} from "../api";
import {
  ActionAnswerRow,
  type ActionAnswerDraft,
} from "../components/ActionAnswerRow";
import { ErrorBlock, LoadingBlock } from "../components/QueryState";
import { flattenGoals, thematicTotals, type FlatGoal } from "../progress-utils";
import type {
  AnswerType,
  QuestionnaireProgress,
  RegisterFlush,
} from "../types";

/** Warm a goal's actions + answers into the query cache (next-goal prefetch). */
function prefetchGoal(qc: QueryClient, goalId: string): Promise<unknown> {
  return Promise.all([
    qc.prefetchQuery(goalActionsQuery(goalId)),
    qc.prefetchQuery(goalAnswersQuery(goalId)),
  ]);
}

/**
 * Focus mode: walk only the *unanswered* practices, one at a time. The queue of
 * goals with unanswered actions is snapshotted on entry (so it doesn't churn as
 * answers land); within each goal we surface the first not-yet-seen action.
 */
export function FocusMode({
  data,
  onContinue,
  registerFlush,
}: {
  data: QuestionnaireProgress;
  onContinue: () => void;
  registerFlush: RegisterFlush;
}) {
  // Stable queue of goals that had unanswered actions when Focus opened.
  const [queue] = useState<FlatGoal[]>(() =>
    flattenGoals(data).filter((g) => g.total > 0 && g.answered < g.total),
  );
  const [qIdx, setQIdx] = useState(0);

  const initialUnanswered = useMemo(
    () => data.total_actions - data.answered_actions,
    // snapshot on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const liveRemaining = data.total_actions - data.answered_actions;
  const index = Math.min(
    Math.max(initialUnanswered - liveRemaining + 1, 1),
    Math.max(initialUnanswered, 1),
  );

  const current = queue[qIdx];
  const advance = () => setQIdx((i) => i + 1);

  // Warm the next goal's actions + answers into the cache while the member works
  // through the current one, so advancing renders from cache instead of showing
  // the loading block. Autosave only invalidates progress/results (not
  // actions/answers), so this prefetched entry survives until it's read.
  const qc = useQueryClient();
  const nextGoalId = queue[qIdx + 1]?.goalId ?? null;
  useEffect(() => {
    if (nextGoalId) void prefetchGoal(qc, nextGoalId);
  }, [qc, nextGoalId]);

  if (!current) {
    return <AllCaughtUp onContinue={onContinue} />;
  }

  return (
    <div className="space-y-5">
      <ThematicRail data={data} currentCssClass={current.cssClass} />
      <FocusGoal
        key={current.goalId}
        goal={current}
        index={index}
        total={initialUnanswered}
        onGoalDone={advance}
        registerFlush={registerFlush}
      />
    </div>
  );
}

// --- Thematic progress rail ---------------------------------------------------

function ThematicRail({
  data,
  currentCssClass,
}: {
  data: QuestionnaireProgress;
  currentCssClass: string;
}) {
  const totals = thematicTotals(data);
  return (
    <div className="flex items-center gap-1 overflow-x-auto rounded-xl border bg-card px-4 py-3">
      {totals.map((tt, i) => {
        const tc = thematicClasses(tt.cssClass);
        const complete = tt.total > 0 && tt.answered >= tt.total;
        const isCurrent = tt.cssClass === currentCssClass;
        return (
          <div key={tt.id} className="flex items-center gap-1">
            {i > 0 && (
              <span
                className={cn(
                  "h-0.5 w-8 shrink-0 rounded-full",
                  complete ? "bg-success" : "bg-border",
                )}
                aria-hidden
              />
            )}
            <span
              title={tt.name}
              className={cn(
                "grid size-9 shrink-0 place-items-center rounded-full text-white transition-all",
                tc.solid,
                !isCurrent && !complete && "opacity-40",
                isCurrent && "ring-2 ring-ring ring-offset-2",
              )}
            >
              {complete ? (
                <Check className="size-4" aria-hidden />
              ) : (
                <span className="text-xs font-bold tabular-nums">
                  {tt.answered}
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// --- Single-goal focus card ---------------------------------------------------

type DraftMap = Record<string, ActionAnswerDraft>;

function draftsToInputs(drafts: DraftMap): BulkAnswerInput[] {
  return Object.entries(drafts)
    .filter(([, d]) => d.answer != null)
    .map(([action, d]) => ({
      action,
      answer: d.answer as AnswerType,
      comment: d.comment,
      show_comment_publicly: d.showCommentPublicly,
      priority: d.priority,
    }));
}

function FocusGoal({
  goal,
  index,
  total,
  onGoalDone,
  registerFlush,
}: {
  goal: FlatGoal;
  index: number;
  total: number;
  onGoalDone: () => void;
  registerFlush: RegisterFlush;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const actions = useGoalActions(goal.goalId);
  const answers = useGoalAnswers(goal.goalId);

  const [drafts, setDrafts] = useState<DraftMap>({});
  const [seen, setSeen] = useState<Set<string>>(new Set());

  const autosave = useAutosave<DraftMap>({
    save: (draftMap, signal) =>
      bulkSubmitAnswers(goal.goalId, draftsToInputs(draftMap), signal),
    shouldSave: (draftMap) => draftsToInputs(draftMap).length > 0,
    onSaved: () => invalidateAfterAnswersSaved(qc),
    onError: () => toast.error(t("questionnaire.saveError")),
  });

  useEffect(() => {
    registerFlush(autosave.flush);
    return () => registerFlush(null);
  }, [registerFlush, autosave.flush]);

  const serverAnswered = useMemo(
    () =>
      new Set(
        (answers.data ?? [])
          .filter((a) => a.action && a.answer != null)
          .map((a) => a.action as string),
      ),
    [answers.data],
  );

  const list = actions.data ?? [];
  const currentAction = list.find(
    (a) => !serverAnswered.has(a.id) && !seen.has(a.id),
  );

  // When nothing is left in this goal, move to the next goal in the queue.
  useEffect(() => {
    if (!actions.isLoading && !answers.isLoading && list.length > 0 && !currentAction) {
      onGoalDone();
    }
  }, [actions.isLoading, answers.isLoading, list.length, currentAction, onGoalDone]);

  if (actions.isLoading || answers.isLoading) return <LoadingBlock />;
  if (actions.error) return <ErrorBlock error={actions.error} />;
  if (answers.error) return <ErrorBlock error={answers.error} />;
  if (!currentAction) return <LoadingBlock />;

  const draft = drafts[currentAction.id] ?? {
    answer: null,
    comment: "",
    showCommentPublicly: false,
    priority: null,
  };

  const update = (next: ActionAnswerDraft) => {
    setDrafts((prev) => {
      const merged = { ...prev, [currentAction.id]: next };
      autosave.save(merged);
      return merged;
    });
  };

  const advance = async () => {
    await autosave.flush();
    setSeen((prev) => new Set(prev).add(currentAction.id));
  };

  const tc = thematicClasses(goal.cssClass);

  return (
    <Card className="mx-auto max-w-3xl overflow-hidden">
      <div className={cn("h-1.5", tc.solid)} aria-hidden />
      <CardContent className="p-6 sm:p-7">
        <div className="flex flex-wrap items-center gap-2">
          <p
            className={cn(
              "text-xs font-bold uppercase tracking-wide",
              tc.ink,
            )}
          >
            {goal.thematicName} · {goal.issueName}
          </p>
          <span className="ml-auto text-sm font-semibold tabular-nums text-muted-foreground">
            {t("questionnaire.answering.focusCount", { index, total })}
          </span>
        </div>

        <h3 className="mt-3 text-xl font-bold tracking-tight">{currentAction.name}</h3>
        <p className="mt-1 text-sm font-medium text-muted-foreground">
          {t("questionnaire.answering.goalLabel")} — {goal.goalName}
        </p>

        <div className="mt-5">
          <ActionAnswerRow
            action={currentAction}
            draft={draft}
            onChange={update}
            variant="bare"
          />
        </div>

        <div className="mt-6 flex items-center justify-between gap-3 border-t pt-5">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void advance()}
              className="text-sm font-semibold text-muted-foreground hover:text-foreground"
            >
              {t("questionnaire.answering.skip")}
            </button>
            <SaveStatus status={autosave.status} />
          </div>
          <Button type="button" onClick={() => void advance()}>
            {t("questionnaire.answering.nextUnanswered")}
            <ArrowRight aria-hidden />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AllCaughtUp({ onContinue }: { onContinue: () => void }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
        <span className="grid size-14 place-items-center rounded-full bg-success/10 text-success">
          <PartyPopper className="size-7" aria-hidden />
        </span>
        <div>
          <h3 className="text-xl font-bold tracking-tight">
            {t("questionnaire.answering.allAnswered")}
          </h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            {t("questionnaire.answering.allAnsweredDesc")}
          </p>
        </div>
        <Button type="button" onClick={onContinue}>
          {t("questionnaire.answering.reviewResults")}
          <ArrowRight aria-hidden />
        </Button>
      </CardContent>
    </Card>
  );
}
